<template>
  <button
    ref="buttonRef"
    class="flex items-center justify-center object-cover rounded-md cursor-pointer outline-1 outline min-w-6 min-h-6 size-full outline-transparent focus:outline-brand focus:hover:outline-transparent"
    :aria-label="ariaLabel || 'Open Dropdown'"
    :aria-expanded="isOpen"
    aria-haspopup="menu"
    :aria-controls="isOpen ? `${id}-options` : undefined"
    @click="toggleDropdown"
    @keydown="handleKeydown"
  >
    <AM3Icon
      :id="id"
      :type="iconType"
      :data-test-id="dataTestId"
    />
    <AM3DropdownPanel
      :target="id"
      :model-value="isOpen"
      :position="position"
      @update:model-value="isOpen = $event"
    >
      <div
        :id="`${id}-options`"
        class="w-full overflow-y-auto dropdown-menu-options max-h-72"
        role="menu"
        :aria-label="ariaLabel || 'Options'"
      >
        <div
          v-for="(option, index) in options"
          :key="getKey(option, index)"
          class="dropdown-menu-option px-4 py-3 min-w-[120px] outline-0 ring-0 focus-visible:outline focus-visible:outline-1 focus-visible:outline-brand text-sm transition-colors duration-75 text-primary"
          v-tooltip.left="getTooltip?.(option)"
          :class="[
            isOptionDisabled(option) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
            isOptionFocused(index) && !isOptionDisabled(option) ? 'bg-brand-light' : (isOptionDisabled(option) ? '' : 'hover:bg-surface'),
          ]"
          :aria-disabled="isOptionDisabled(option) || undefined"
          :data-test-id="generateDataTestId(dataTestId, 'option', getLabel(option))"
          role="menuitem"
          :tabindex="-1"
          @click="(event) => onClick(option, event)"
        >
          <slot
            name="option"
            :option="option"
          >
            {{ getLabel(option) }}
          </slot>
        </div>
        <slot />
      </div>
    </AM3DropdownPanel>
  </button>
</template>

<script setup lang="ts" generic="O, V">
import { ref, computed, toRef } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import AM3Icon from './icon/AM3Icon.vue'
import AM3DropdownPanel from './AM3DropdownPanel.vue'
import type { DropdownPosition } from './AM3DropdownPanel.vue'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'
import { useKeyboardDropdownNavigation } from '@/composables/useKeyboardDropdownNavigation'

/** Props (generic over option O and value V) */
interface Props<O, V> {
  options: O[] // Dropdown options
  getValue: (option: O) => V // Extract value from option
  id?: string // Trigger element ID
  getLabel?: (option: O) => string // Extract label (default: String)
  getKey?: (option: O, index: number) => string // Key generator
  getDisabled?: (option: O) => boolean // Disabled options stay listed but can't be chosen
  getTooltip?: (option: O) => string | undefined // Hover text, e.g. why an option is disabled
  iconType?: string // Trigger icon
  position?: DropdownPosition // Panel position
  dataTestId?: string // For testing
  ariaLabel?: string
}

/** Props with defaults */
const props = withDefaults(defineProps<Props<O, V>>(), {
  id: () => `dropdown-menu-${uuidv4()}`,
  getLabel: (o: O) => String((o as unknown) ?? ''),
  getKey: (_o: O, index: number) => `dropdown-menu-option-${index}`,
  iconType: 'ellipsis-vertical',
  position: 'bottom-left' as DropdownPosition,
  dataTestId: 'dropdown-menu',
  ariaLabel: '',
})

/** Emits */
const emit = defineEmits<{
  (e: 'change', value: V): void
}>()

/** State */
const isOpen = ref(false)
const buttonRef = ref<HTMLButtonElement | null>(null)

// Map generic options to { value: index, label } for keyboard navigation composable
const mappedOptions = computed(() =>
  props.options.map((option, index) => ({
    value: index,
    label: props.getLabel(option),
  })),
)

// Dropdown menu has no persistent selection — always start focus at first item
const noSelection = ref<number>(-1)

const isOptionDisabled = (option: O) => props.getDisabled?.(option) ?? false

const { handleKeydown, isOptionFocused } = useKeyboardDropdownNavigation({
  options: mappedOptions,
  modelValue: noSelection,
  isOpen,
  buttonRef,
  onSelect: (mapped) => {
    const option = props.options[mapped.value]
    if (option !== undefined && !isOptionDisabled(option)) {
      emit('change', props.getValue(option))
      isOpen.value = false
    }
  },
})

/** Methods */
function toggleDropdown(event: MouseEvent) {
  event.stopPropagation()
  isOpen.value = !isOpen.value
}

function onClick(option: O, event: MouseEvent) {
  event.stopPropagation()
  if (isOptionDisabled(option)) return
  emit('change', props.getValue(option))
  isOpen.value = false
}
</script>
